import React, { useState } from 'react';

interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

interface FormStatus {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState<FormStatus>({
    isLoading: false,
    isSuccess: false,
    isError: false,
    errorMessage: ''
  });

  const webhookUrl = 'https://flujos.mariojarrinv.tech/webhook-test/868498a7-1b52-4344-9f8c-7bce9c0406f5';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Limpiar mensajes de estado cuando el usuario empiece a escribir
    if (formStatus.isSuccess || formStatus.isError) {
      setFormStatus({
        isLoading: false,
        isSuccess: false,
        isError: false,
        errorMessage: ''
      });
    }
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setFormStatus({
        isLoading: false,
        isSuccess: false,
        isError: true,
        errorMessage: 'El nombre es requerido'
      });
      return false;
    }
    if (!formData.email.trim()) {
      setFormStatus({
        isLoading: false,
        isSuccess: false,
        isError: true,
        errorMessage: 'El email es requerido'
      });
      return false;
    }
    if (!formData.message.trim()) {
      setFormStatus({
        isLoading: false,
        isSuccess: false,
        isError: true,
        errorMessage: 'El mensaje es requerido'
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setFormStatus({
      isLoading: true,
      isSuccess: false,
      isError: false,
      errorMessage: ''
    });

    try {
      const payload = {
        nombre: formData.name,
        email: formData.email,
        telefono: formData.phone,
        mensaje: formData.message,
        fecha: new Date().toISOString(),
        origen: 'TopElectronica Website'
      };

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setFormStatus({
          isLoading: false,
          isSuccess: true,
          isError: false,
          errorMessage: ''
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error('Error en el servidor');
      }
    } catch (error) {
      console.error('Error enviando formulario:', error);
      setFormStatus({
        isLoading: false,
        isSuccess: false,
        isError: true,
        errorMessage: 'Error al enviar el mensaje. Por favor, inténtalo de nuevo.'
      });
    }
  };

  return (
    <section id="contacto" className="contact">
      <div className="contact-container">
        <h2 className="section-title">Información de Contacto</h2>
        <div className="contact-grid">
          <div className="contact-info">
            <h3>Contáctanos</h3>
            <div className="contact-item">
              <div className="contact-icon">📧</div>
              <div>
                <strong>Email:</strong><br />
                info@topelectronicaec.com
              </div>
            </div>
            <div className="contact-item">
              <div className="contact-icon">📱</div>
              <div>
                <strong>Teléfono:</strong><br />
                +593 96 937 2133
              </div>
            </div>
            <div className="contact-item">
              <div className="contact-icon">🌐</div>
              <div>
                <strong>Síguenos:</strong><br />
                Visítanos en nuestras redes sociales
              </div>
            </div>
          </div>
          
          <div className="contact-form">
            <h3>Envía un Mensaje</h3>
            
            {/* Status Messages */}
            {formStatus.isSuccess && (
              <div className="form-message success">
                ✅ ¡Mensaje enviado correctamente! Nos pondremos en contacto contigo pronto.
              </div>
            )}
            
            {formStatus.isError && (
              <div className="form-message error">
                ❌ {formStatus.errorMessage}
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Nombre Completo *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={formStatus.isLoading}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Correo Electrónico *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={formStatus.isLoading}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Teléfono</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={formStatus.isLoading}
                  placeholder="+593 XX XXX XXXX"
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Mensaje *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  disabled={formStatus.isLoading}
                  required
                  placeholder="Describe tu consulta o requerimiento sobre equipos médicos..."
                ></textarea>
              </div>
              <button 
                type="submit" 
                className={`cta-button ${formStatus.isLoading ? 'loading' : ''}`}
                disabled={formStatus.isLoading}
              >
                {formStatus.isLoading ? (
                  <>
                    <span className="spinner"></span>
                    Enviando...
                  </>
                ) : (
                  'Enviar Mensaje'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
